import qs from 'qs'
import { CUSTOMER_TYPES, ANYWHERE_PLACE_ID } from '../constants/search'

const basePath = '/people-like-me'

export interface CustomerTypeData {
  customerType: CUSTOMER_TYPES;
  label: string;
  heroImageId: string;
  heroImageGravity?: 'auto' | 'center' | 'east' | 'north' | 'northeast' | 'northwest' | 'south' | 'southeast' | 'southwest' | 'west';
  categoryImageId: string;
  altText: string;
  salesBanner: string;
  queryString: string;
  sortBy: App.HotelTrendingSortByOptions;
  holidayTypesScoped?: string[];
  imageId: string;
  path: string;
  singular: string;
}

export const CUSTOMER_TYPE_DATA_SOLO: CustomerTypeData = {
  customerType: CUSTOMER_TYPES.SOLO,
  label: 'Solo Travellers',
  heroImageId: 'lady-looking-at-balloons',
  categoryImageId: 'people-like-me-solo-category',
  altText: 'Travel inspiration for solo travellers',
  salesBanner: 'solo travellers',
  queryString: 'solo',
  sortBy: 'customer.type.solo',
  imageId: 'people-like-me-solo-category',
  path: `${basePath}?customerType=solo`,
  singular: 'Solo Traveller',
}

export const CUSTOMER_TYPE_DATA_COUPLE: CustomerTypeData = {
  customerType: CUSTOMER_TYPES.COUPLE,
  label: 'Couples',
  heroImageId: 'couple-standing-on-infinite-pool-cropped',
  heroImageGravity: 'north',
  categoryImageId: 'people-like-me-couple-category',
  altText: 'Travel inspiration for couples',
  salesBanner: 'couples',
  queryString: 'couple',
  sortBy: 'customer.type.couple',
  holidayTypesScoped: ['Romantic'],
  imageId: 'people-like-me-couple-category',
  path: `${basePath}?customerType=couple`,
  singular: 'Couple',
}

export const CUSTOMER_TYPE_DATA_GROUP: CustomerTypeData = {
  customerType: CUSTOMER_TYPES.GROUP,
  label: 'Groups',
  heroImageId: 'friends-dinner-at-beach-cropped',
  categoryImageId: 'people-like-me-group-category',
  altText: 'Travel inspiration for groups',
  salesBanner: 'groups',
  queryString: 'group',
  sortBy: 'customer.type.group',
  holidayTypesScoped: ['Group'],
  imageId: 'people-like-me-group-category',
  path: `${basePath}?customerType=group`,
  singular: 'Group',
}

export const CUSTOMER_TYPE_DATA_FAMILY: CustomerTypeData = {
  customerType: CUSTOMER_TYPES.FAMILY,
  label: 'Families',
  heroImageId: 'three-kids-at-beach-cropped',
  categoryImageId: 'people-like-me-family-category',
  altText: 'Travel inspiration for families',
  salesBanner: 'families',
  queryString: 'family',
  sortBy: 'customer.type.family',
  holidayTypesScoped: ['Family Friendly'],
  imageId: 'people-like-me-family-category',
  path: `${basePath}?customerType=family`,
  singular: 'Family',
}

export const CUSTOMER_TYPE_DATA_LIST: CustomerTypeData[] = [
  CUSTOMER_TYPE_DATA_SOLO,
  CUSTOMER_TYPE_DATA_COUPLE,
  CUSTOMER_TYPE_DATA_GROUP,
  CUSTOMER_TYPE_DATA_FAMILY,
]

export function getCustomerTypeData(customerType: CUSTOMER_TYPES): CustomerTypeData {
  const found = CUSTOMER_TYPE_DATA_LIST.find(data => data.customerType === customerType)

  if (!found) {
    return CUSTOMER_TYPE_DATA_FAMILY
  }

  return found
}

export function getSortByCustomerType(customerType: CUSTOMER_TYPES): App.HotelTrendingSortByOptions {
  const { sortBy } = getCustomerTypeData(customerType)
  return sortBy
}

export function getSaleBannerText(customerType: CUSTOMER_TYPES, saleCount: number): string {
  if (!saleCount) {
    return ''
  }

  const { salesBanner: customerTypeString } = getCustomerTypeData(customerType)

  return `${saleCount} ${customerTypeString}`
}

export function getFilterChipLabel(defaultLabel: string, selectedFilterOptions: Array<string>): string {
  if (!selectedFilterOptions?.length) {
    return defaultLabel
  }

  const firstOption = selectedFilterOptions[0]
  if (selectedFilterOptions.length === 1) {
    return firstOption
  }

  return `${firstOption} +${selectedFilterOptions.length - 1}`
}

export function getCustomerType(searchString: string): CUSTOMER_TYPES {
  const queryResult = qs.parse(searchString, { ignoreQueryPrefix: true }) as { [key: string]: string }

  const found = CUSTOMER_TYPE_DATA_LIST.find(data => data.queryString === queryResult.customerType)

  if (!found) {
    return CUSTOMER_TYPE_DATA_FAMILY.customerType
  }

  return found.customerType
}

export function getCustomerTypePanel(): Array<{ imageId: string, path: string, label: string}> {
  return CUSTOMER_TYPE_DATA_LIST.map(data => ({
    imageId: data.imageId,
    path: data.path,
    label: data.singular,
  }))
}

export interface CustomerTypeDisplay {
  label: string,
  heroImageId: string,
  heroImageGravity?: CustomerTypeData['heroImageGravity'],
  categoryImageId: string,
  altText: string,
}

export function mapCustomerTypeToDisplay(customerType: CUSTOMER_TYPES): CustomerTypeDisplay {
  const data = getCustomerTypeData(customerType)

  return {
    label: data.label,
    heroImageId: data.heroImageId,
    categoryImageId: data.categoryImageId,
    altText: data.altText,
  }
}

export function setSearchFilters(filters: App.OfferListFilters, customerType: CUSTOMER_TYPES): App.OfferListFilters {
  const plmFilters = {
    ...filters,
    destinationId: filters.destinationId ?? ANYWHERE_PLACE_ID,
    checkIn: undefined,
    checkOut: undefined,
  }

  if (!plmFilters.rooms || !plmFilters.rooms.length) {
    plmFilters.rooms = [{ adults: 1 }]
  }

  if (!plmFilters.sortBy) {
    const data = getCustomerTypeData(customerType)

    plmFilters.sortBy = data.sortBy
    plmFilters.holidayTypesScoped = data.holidayTypesScoped
  }

  return plmFilters
}
